import $ from "jquery";
import config from "flow/config";
import dataService from "flow/data/service";
import manifestManager from "flow/utils/manifestManager";
import metaCache from "flow/utils/metaCache";
import userContext from "flow/viewModels/userContext";
import sysLogger from "flow/sysLogger";
import environment from "flow/configuration/environment";
import ProductSelector from "flow/init/ProductSelector";
import * as builtInManifestLoader from "flow/init/builtInManifestsLoader";
import lateExtensionLoader from "flow/init/lateExtensionLoader";
import * as localization from "flow/localization/localization";
import session from "flow/session/session";
import { setup as setupSystemInformation } from "flow/systeminfo/systemInformation";
import MyFlow from "flow/MyFlow";
import { Dictionary } from "flow/api/types";
import themeCache from "flow/caches/themeCache";
import { applyEnvironmentOverridesFromUrlAndSessionStorage } from "./urlOverrides";

export function readWorkspace(myFlow: MyFlow): JQueryPromise<boolean> {
    const dfd = myFlow.deferred<boolean>();
    dataService
        .getWorkspace(myFlow)
        .then(function (workspaceData) {
            mergeMetaConfigs(workspaceData.metaConfigs);

            config.logoutUrl = workspaceData.logoutUrl;

            environment.set(environment.keys.userId, workspaceData.userId);
            environment.set(environment.keys.userName, workspaceData.userName);
            environment.set(environment.keys.environmentBanner, myFlow.lookup(config.metaKeys.notProdEnvironment, ""));
            environment.set(environment.keys.environmentDescription, workspaceData.environmentDescription);
            environment.set(environment.keys.productionMode, workspaceData.productionMode);
            environment.set(environment.keys.useProductSelector, workspaceData.useProductSelector);

            if (!environment.get(environment.keys.useProductSelector)) {
                environment.set(environment.keys.showProductSelector, false);
            }

            if (workspaceData.selectedProduct && workspaceData.selectedProduct.id) {
                environment.set(environment.keys.productIdRemember, workspaceData.selectedProduct.id);
                environment.set(environment.keys.productPayloadRemember, workspaceData.selectedProduct.payload);
            }

            environment.set(environment.keys.singleModule, workspaceData.singleModule);
            environment.set(environment.keys.crossLaunch, metaCache.lookup("Common.Features.Flow3_CrossLaunch", false));

            environment.set(environment.keys.themeSelector, myFlow.lookup(config.metaKeys.themeSelector, false));

            themeCache.set(workspaceData.themes);
            environment.set(environment.keys.selectedTheme, workspaceData.selectedTheme);

            environment.set(environment.keys.debug, metaCache.lookup("FlowHtml.Settings.Debug", false));
            environment.set(environment.keys.debugEvents, metaCache.lookup("FlowHtml.Settings.DebugEvents", false));
            environment.set(
                environment.keys.debugInactivity,
                metaCache.lookup("FlowHtml.Settings.DebugInactivity", false)
            );
            environment.set(environment.keys.debugOrder, metaCache.lookup("FlowHtml.Settings.DebugOrder", false));

            environment.set(environment.keys.inspector, myFlow.lookup("FlowHtml.Settings.Inspector", false));

            environment.set(environment.keys.logoutUrl, workspaceData.logoutUrl);
            environment.set(environment.keys.runtimeContext, workspaceData.runtimeContext);

            environment.set(
                environment.keys.discoveryAjaxTimeout,
                metaCache.lookup(
                    "FlowHtml.Settings.DiscoveryAjaxTimeout",
                    environment.get(environment.keys.discoveryAjaxTimeout)
                )
            );

            environment.set(
                environment.keys.genericAjaxTimeout,
                metaCache.lookup(
                    "FlowHtml.Settings.GenericAjaxTimeout",
                    environment.get(environment.keys.genericAjaxTimeout)
                )
            );

            environment.set(
                environment.keys.heartbeatInterval,
                metaCache.lookup(
                    "FlowHtml.Settings.HeartbeatInterval",
                    environment.get(environment.keys.heartbeatInterval)
                )
            );

            environment.set(
                environment.keys.heartbeatTimeout,
                metaCache.lookup(
                    "FlowHtml.Settings.HeartbeatTimeout",
                    environment.get(environment.keys.heartbeatTimeout)
                )
            );

            environment.set(
                environment.keys.homeConfiguration,
                metaCache.lookup("Common.Features.Flow_HomeConfiguration", false)
            );

            $.ajaxSetup({
                timeout: environment.get(environment.keys.genericAjaxTimeout) as number
            });

            environment.set(environment.keys.inactivityTimeout, workspaceData.inactivityTimeout);
            environment.set(environment.keys.inactivityDialogTimeout, workspaceData.inactivityDialogTimeout);
            environment.set(
                environment.keys.inactivityDialogTimeoutUnsaved,
                workspaceData.inactivityDialogTimeoutUnsaved
            );

            environment.set(environment.keys.systemInformation, workspaceData.systemInformation);
            environment.set(environment.keys.systemVersion, workspaceData.systemVersion);

            environment.set(
                environment.keys.sessionRecovery,
                metaCache.lookup("FlowHtml.Settings.SessionRecovery", false)
            );

            userContext.setup(myFlow);
            localization.setup(environment.getUiCulture());

            manifestManager.setBlacklist(workspaceData.roleBlacklist);

            manifestManager.setDescription(config.moduleId, workspaceData.moduleDescription);
            manifestManager.addManifests(config.moduleId, [workspaceData.inspectorDialogManifest], undefined);
            manifestManager.addManifests(config.moduleId, workspaceData.userContextTabManifests, undefined);

            sysLogger.addClientLogLevels(config.moduleId, workspaceData.clientLogLevel);
            manifestManager.addManifests(config.moduleId, [workspaceData.creditComponentManifest], undefined);

            manifestManager.addLicenseBundles(workspaceData.licenseBundles);

            applyEnvironmentOverridesFromUrlAndSessionStorage();

            builtInManifestLoader.saveManifestsForLaterRegistration(workspaceData);

            session.setup(myFlow);
            setupSystemInformation();

            const productSelector = new ProductSelector(myFlow);
            return productSelector
                .select()
                .then(flow2Boot => {
                    builtInManifestLoader.registerComponents();
                    return flow2Boot;
                })
                .then(flow2Boot => lateExtensionLoader.load(myFlow, flow2Boot))
                .then(dfd.resolve)
                .catch(dfd.reject);
        })
        .catch(e => {
            handleWorkspaceReadError(myFlow, e);
            dfd.reject();
        });

    return dfd.promise();
}

function mergeMetaConfigs(metaConfigs: Dictionary<any>[]) {
    $(metaConfigs).each(function (_, metaConfig) {
        metaCache.add(metaConfig);
    });
}

function handleWorkspaceReadError(myFlow: MyFlow, args: any[]) {
    myFlow.fatal(new Error("Failed to read workspace data:"));
    myFlow.logObjectsAsErrors.apply(myFlow, args);

    myFlow.showErrorDialog(
        "I'm very sorry!",
        "I've failed to read basic settings, including preferred language.<br>" +
            "Therefore I'm unable to continue loading Flow."
    );
}
